export type PopoverPlacement = 'above' | 'below';

export const POPOVER_GAP_PX = 6;

const VIEWPORT_PADDING_PX = 8;
const PANEL_MAX_HEIGHT_PX = 320;

export interface PopoverLayout {
  placement: PopoverPlacement;
  top: number;
  left: number;
  maxHeight: number;
}

export interface AnchorMetrics {
  top: number;
  bottom: number;
  left: number;
}

export interface PanelDimensions {
  height: number;
  width: number;
}

export const anchorMetricsFromRect = (rect: DOMRect): AnchorMetrics => ({
  top: Number(rect.top),
  bottom: Number(rect.bottom),
  left: Number(rect.left),
});

export const panelDimensionsFromElement = (element: HTMLElement): PanelDimensions => ({
  height: Number(element.offsetHeight),
  width: Number(element.offsetWidth),
});

export const clampPopoverLeft = (preferredLeft: number, panelWidth: number): number => {
  const maxLeft = window.innerWidth - VIEWPORT_PADDING_PX - panelWidth;
  return Math.max(VIEWPORT_PADDING_PX, Math.min(preferredLeft, maxLeft));
};

export const computePopoverLayout = (
  anchor: AnchorMetrics,
  panelHeight: number,
  panelWidth: number,
  preferredPlacement: PopoverPlacement,
): PopoverLayout => {
  const left = clampPopoverLeft(anchor.left, panelWidth);
  const spaceAbove = anchor.top - POPOVER_GAP_PX - VIEWPORT_PADDING_PX;
  const spaceBelow = window.innerHeight - anchor.bottom - POPOVER_GAP_PX - VIEWPORT_PADDING_PX;

  const fitsAbove = anchor.top - POPOVER_GAP_PX - panelHeight >= VIEWPORT_PADDING_PX;
  const fitsBelow =
    anchor.bottom + POPOVER_GAP_PX + Math.min(panelHeight, PANEL_MAX_HEIGHT_PX) <=
    window.innerHeight - VIEWPORT_PADDING_PX;

  let placement: PopoverPlacement = preferredPlacement;
  if (preferredPlacement === 'above' && !fitsAbove) {
    placement = 'below';
  } else if (preferredPlacement === 'below' && !fitsBelow && spaceAbove > spaceBelow) {
    placement = 'above';
  }

  const top = placement === 'above' ? anchor.top : anchor.bottom;
  const maxHeight = Math.min(PANEL_MAX_HEIGHT_PX, Math.max(0, placement === 'above' ? spaceAbove : spaceBelow));

  return { placement, top, left, maxHeight };
};
